import CloseIcon from "@mui/icons-material/Close";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import ElectricalServicesIcon from "@mui/icons-material/ElectricalServices";
import EventIcon from "@mui/icons-material/Event";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ProjectWorkflowFoundationApi from "../../../infrastructure/api/projectWorkflowFoundation";
import RequestAssignmentApi from "../../../infrastructure/api/requestAssignment";
import { toJalaliFullDate } from "../../middleware/middleware";
import ContractorSelection from "../../pages/SLN/customer/ContractorSelection";
import RequestFollowUpTimeline from "./RequestFollowUpTimeline";
import WorkflowV2IntegrationPanel from "./WorkflowV2IntegrationPanel";
import { getCurrentUserId } from "./requestAssignmentDisplay";
import { pickActiveSiteVisitAssignment } from "../../pages/SLN/shared/projectWorkspace/loanMehrsanStage/initialSiteVisitAssignmentUtils";

const EQUIPMENT_SUPPLY_MODE_LABELS = {
  1: "پیمانکار",
  2: "تأمین‌کننده",
};

const formatContractorLookupLabel = (item) => {
  if (!item) return "—";
  const title = String(item.title ?? item.Title ?? "").trim();
  const company = String(item.companyName ?? item.CompanyName ?? "").trim();
  if (company && title) {
    if (title.includes(company) || title.includes("—") || title.includes("-")) {
      return title;
    }
    return `${company} — ${title}`;
  }
  return title || company || "—";
};

const lookupOptionTitle = (options, id) => {
  if (id == null || id === "") return "—";
  const match = (options || []).find(
    (item) => String(item.id ?? item.Id) === String(id)
  );
  return formatContractorLookupLabel(match);
};

const pickPlanField = (plan, ...keys) => {
  if (!plan) return null;
  for (const key of keys) {
    const value = plan[key];
    if (value != null && value !== "") return value;
  }
  return null;
};

const hasPersistedOperationalPlanRecord = (plan) =>
  Boolean(plan && (plan.id || plan.Id));

const RequestInfoCard = ({
  request,
  source = "Management",
  followUps = [],
  hideWorkflowV2Panel = false,
  workspaceQuotesOnly = false,
  workspaceOperationalBundle = false,
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const showContractorSelectionButton =
    request?.requestConfirm === "InProcess" ||
    request?.requestConfirmTitle?.includes("در حال پردازش");
  const canShowApplicantGoals = source !== "Contractor" && source !== "Supplier";
  const canShowOwnershipStatus = source !== "Supplier";
  const canShowFollowUps = source === "Management" || source === "SolarentOperator";
  const [assignmentFeatureEnabled, setAssignmentFeatureEnabled] = useState(false);

  useEffect(() => {
    if (!canShowFollowUps) return;
    RequestAssignmentApi.getStatus(source).then((status) => {
      setAssignmentFeatureEnabled(status.enabled);
    });
  }, [canShowFollowUps, source]);

  const canShowAssignment = canShowFollowUps && assignmentFeatureEnabled;
  const currentUserId = getCurrentUserId();
  const assignedId =
    request?.assignedOperatorUserId ?? request?.AssignedOperatorUserId ?? null;
  const assignedName =
    request?.assignedOperatorFullName ?? request?.AssignedOperatorFullName ?? "";
  const assignmentOwnerLabel = !assignedId
    ? "بدون مسئول"
    : currentUserId && assignedId === currentUserId
      ? "شما"
      : assignedName || "—";

  const canShowOperationalPlanSection =
    (source === "Management" || source === "SolarentOperator") && !!request?.id;
  const [operationalPlan, setOperationalPlan] = useState(null);
  const [operationalPlanForm, setOperationalPlanForm] = useState({
    equipmentSupplyMode: "",
    executionContractorInfoId: "",
    supplierInfoId: "",
    description: "",
  });
  const [operationalPlanLookups, setOperationalPlanLookups] = useState({
    contractors: [],
    siteVisitContractors: [],
    supervisors: [],
    suppliers: [],
  });
  const [operationalPlanLoading, setOperationalPlanLoading] = useState(false);
  const [operationalPlanSaving, setOperationalPlanSaving] = useState(false);
  const [operationalPlanError, setOperationalPlanError] = useState("");
  const [operationalPlanSuccess, setOperationalPlanSuccess] = useState("");
  const [operationalPlanUiMode, setOperationalPlanUiMode] = useState("create");
  const assignmentsSectionRef = useRef(null);
  const [assignmentList, setAssignmentList] = useState([]);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [quoteList, setQuoteList] = useState([]);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteActionLoadingId, setQuoteActionLoadingId] = useState(null);
  const [quoteContractLoadingId, setQuoteContractLoadingId] = useState(null);
  const [quoteRejectDialogOpen, setQuoteRejectDialogOpen] = useState(false);
  const [quoteRejectTargetId, setQuoteRejectTargetId] = useState(null);
  const [quoteRejectReason, setQuoteRejectReason] = useState("");
  const [quoteActionError, setQuoteActionError] = useState("");
  const [quoteActionSuccess, setQuoteActionSuccess] = useState("");
  const [quoteNegotiationOpenId, setQuoteNegotiationOpenId] = useState(null);
  const [negotiationThread, setNegotiationThread] = useState([]);
  const [negotiationLoading, setNegotiationLoading] = useState(false);
  const [negotiationSending, setNegotiationSending] = useState(false);
  const [negotiationError, setNegotiationError] = useState("");
  const [negotiationForm, setNegotiationForm] = useState({
    messageType: "1",
    message: "",
    proposedPrice: "",
    proposedDurationDays: "",
  });

  const negotiationMessageTypeOptions = [
    { value: "1", label: "پیام" },
    { value: "2", label: "پیشنهاد متقابل (Counter Offer)" },
    { value: "3", label: "درخواست شفاف‌سازی" },
    { value: "4", label: "تغییر Scope" },
  ];

  const QUOTE_STATUS_SUBMITTED = 2;
  const QUOTE_STATUS_APPROVED = 3;
  const QUOTE_STATUS_REJECTED = 4;
  const QUOTE_STATUS_NEGOTIATING = 6;
  const QUOTE_TYPE_EXECUTION = 3;

  const parseToNumberOrNull = (value) => {
    if (value === "" || value === null || value === undefined) return null;
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
  };

  const mapPlanToForm = (plan) => {
    if (!plan) {
      return {
        equipmentSupplyMode: "",
        executionContractorInfoId: "",
        supplierInfoId: "",
        description: "",
      };
    }

    const legacyContractorId =
      plan.contractorInfoId != null ? String(plan.contractorInfoId) : "";
    const executionContractorId =
      plan.executionContractorInfoId != null
        ? String(plan.executionContractorInfoId)
        : plan.equipmentContractorInfoId != null
          ? String(plan.equipmentContractorInfoId)
          : legacyContractorId;

    return {
      equipmentSupplyMode:
        plan.equipmentSupplyMode != null ? String(plan.equipmentSupplyMode) : "",
      executionContractorInfoId: executionContractorId,
      supplierInfoId:
        plan.supplierInfoId != null ? String(plan.supplierInfoId) : "",
      description: plan.description || "",
    };
  };

  const loadOperationalPlanData = useCallback(async () => {
    if (!canShowOperationalPlanSection) return;

    try {
      setOperationalPlanLoading(true);
      setAssignmentLoading(true);
      setQuoteLoading(true);
      setOperationalPlanError("");
      setOperationalPlanSuccess("");

      const [planRes, lookupRes, assignmentRes, quoteRes] = await Promise.all([
        ProjectWorkflowFoundationApi.getOperationalPlanByRequestId(source, request.id),
        ProjectWorkflowFoundationApi.getOperationalPlanLookups(source),
        ProjectWorkflowFoundationApi.getAssignmentsByRequestId(source, request.id),
        ProjectWorkflowFoundationApi.getQuotesByRequestId(source, request.id),
      ]);

      if (!lookupRes.ok) {
        setOperationalPlanLookups({
          contractors: [],
          siteVisitContractors: [],
          supervisors: [],
          suppliers: [],
        });
      } else {
        setOperationalPlanLookups({
          contractors: lookupRes.data?.contractors || lookupRes.data?.Contractors || [],
          siteVisitContractors:
            lookupRes.data?.siteVisitContractors || lookupRes.data?.SiteVisitContractors || [],
          supervisors: lookupRes.data?.supervisors || lookupRes.data?.Supervisors || [],
          suppliers: lookupRes.data?.suppliers || lookupRes.data?.Suppliers || [],
        });
      }

      if (!planRes.ok) {
        setOperationalPlanError(
          planRes.message || "خطا در دریافت برنامه عملیاتی پروژه."
        );
      } else {
        setOperationalPlanError("");
        const planData = planRes.data || null;
        setOperationalPlan(planData);
        setOperationalPlanForm(mapPlanToForm(planData));
      }

      if (assignmentRes.ok) {
        setAssignmentList(assignmentRes.data || []);
      }
      if (quoteRes.ok) {
        setQuoteList(quoteRes.data || []);
      }
    } catch {
      setOperationalPlanError("خطا در دریافت اطلاعات برنامه عملیاتی پروژه.");
    } finally {
      setOperationalPlanLoading(false);
      setAssignmentLoading(false);
      setQuoteLoading(false);
    }
  }, [canShowOperationalPlanSection, request?.id, source]);

  useEffect(() => {
    loadOperationalPlanData();
  }, [loadOperationalPlanData]);

  const hasPersistedOperationalPlan = hasPersistedOperationalPlanRecord(operationalPlan);

  useEffect(() => {
    if (!workspaceOperationalBundle || operationalPlanLoading) return;
    setOperationalPlanUiMode((prev) => {
      if (prev === "edit") return prev;
      return hasPersistedOperationalPlan ? "view" : "create";
    });
  }, [
    workspaceOperationalBundle,
    operationalPlanLoading,
    hasPersistedOperationalPlan,
  ]);

  const showOperationalPlanSummary =
    workspaceOperationalBundle &&
    operationalPlanUiMode === "view" &&
    hasPersistedOperationalPlan;

  const showOperationalPlanForm =
    !workspaceOperationalBundle ||
    operationalPlanUiMode === "create" ||
    operationalPlanUiMode === "edit";

  const operationalPlanSummary = useMemo(() => {
    if (!hasPersistedOperationalPlan) return null;

    const supplyMode = String(
      pickPlanField(operationalPlan, "equipmentSupplyMode", "EquipmentSupplyMode") ?? ""
    );
    const executionContractorId = pickPlanField(
      operationalPlan,
      "executionContractorInfoId",
      "ExecutionContractorInfoId",
      "equipmentContractorInfoId",
      "EquipmentContractorInfoId"
    );
    const supplierId = pickPlanField(
      operationalPlan,
      "supplierInfoId",
      "SupplierInfoId"
    );

    return {
      equipmentSupply: EQUIPMENT_SUPPLY_MODE_LABELS[supplyMode] || "—",
      supplyContractor:
        supplyMode === "1"
          ? lookupOptionTitle(operationalPlanLookups.contractors, executionContractorId)
          : null,
      executionContractor: lookupOptionTitle(
        operationalPlanLookups.contractors,
        executionContractorId
      ),
      supplier:
        supplyMode === "2"
          ? lookupOptionTitle(operationalPlanLookups.suppliers, supplierId)
          : null,
      description:
        pickPlanField(operationalPlan, "description", "Description") || "—",
      savedAt:
        pickPlanField(operationalPlan, "updateOn", "UpdateOn", "createOn", "CreateOn") ||
        null,
    };
  }, [hasPersistedOperationalPlan, operationalPlan, operationalPlanLookups]);

  const activeSiteVisitAssignment = useMemo(
    () => pickActiveSiteVisitAssignment(assignmentList),
    [assignmentList]
  );

  const activeAssignmentCount = useMemo(
    () => (assignmentList || []).filter((item) => item?.isActive).length,
    [assignmentList]
  );

  const handleEditOperationalPlan = () => {
    setOperationalPlanForm(mapPlanToForm(operationalPlan));
    setOperationalPlanError("");
    setOperationalPlanSuccess("");
    setOperationalPlanUiMode("edit");
  };

  const handleCancelOperationalPlanEdit = () => {
    setOperationalPlanForm(mapPlanToForm(operationalPlan));
    setOperationalPlanError("");
    setOperationalPlanSuccess("");
    setOperationalPlanUiMode("view");
  };

  const scrollToAssignments = () => {
    assignmentsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleOperationalPlanFieldChange = (field, value) => {
    setOperationalPlanSuccess("");
    setOperationalPlanError("");
    setOperationalPlanForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveOperationalPlan = async () => {
    if (!request?.id) return;

    try {
      setOperationalPlanSaving(true);
      setOperationalPlanError("");
      setOperationalPlanSuccess("");

      const payload = {
        id: operationalPlan?.id || 0,
        guid: operationalPlan?.guid || "00000000-0000-0000-0000-000000000000",
        requestId: request.id,
        equipmentSupplyMode: parseToNumberOrNull(
          operationalPlanForm.equipmentSupplyMode
        ),
        projectExecutionMode: operationalPlan?.projectExecutionMode || 0,
        projectOperationalPlanStatus:
          operationalPlan?.projectOperationalPlanStatus || 1,
        contractorInfoId: parseToNumberOrNull(
          operationalPlanForm.executionContractorInfoId
        ),
        executionContractorInfoId: parseToNumberOrNull(
          operationalPlanForm.executionContractorInfoId
        ),
        equipmentContractorInfoId: parseToNumberOrNull(
          operationalPlanForm.executionContractorInfoId
        ),
        supplierInfoId: parseToNumberOrNull(operationalPlanForm.supplierInfoId),
        ownerUserId: operationalPlan?.ownerUserId || null,
        description: operationalPlanForm.description || "",
        effectiveFrom: operationalPlan?.effectiveFrom || null,
        effectiveTo: operationalPlan?.effectiveTo || null,
        isActive: operationalPlan?.isActive ?? true,
      };

      const res = await ProjectWorkflowFoundationApi.upsertOperationalPlan(
        source,
        payload
      );

      if (!res.ok) {
        setOperationalPlanError(
          res.message || "ذخیره برنامه عملیاتی با خطا مواجه شد."
        );
        return;
      }

      const savedPlan = res.data || null;
      setOperationalPlan(savedPlan);
      setOperationalPlanForm(mapPlanToForm(savedPlan));
      const assignmentRes = await ProjectWorkflowFoundationApi.getAssignmentsByRequestId(
        source,
        request.id
      );
      if (assignmentRes.ok) {
        setAssignmentList(assignmentRes.data || []);
      }
      const quoteRes = await ProjectWorkflowFoundationApi.getQuotesByRequestId(
        source,
        request.id
      );
      if (quoteRes.ok) {
        setQuoteList(quoteRes.data || []);
      }
      setOperationalPlanSuccess("برنامه عملیاتی پروژه با موفقیت ذخیره شد.");
      if (workspaceOperationalBundle) {
        setOperationalPlanUiMode("view");
      }
    } catch {
      setOperationalPlanError("خطا در ذخیره برنامه عملیاتی پروژه.");
    } finally {
      setOperationalPlanSaving(false);
    }
  };

  const needsExecutionContractorForSupply =
    String(operationalPlanForm.equipmentSupplyMode) === "1";
  const needsSupplierForSupply =
    String(operationalPlanForm.equipmentSupplyMode) === "2";
  const assignmentTypeLabel = (row) =>
    row?.projectAssignmentTypeTitle ||
    (String(row?.projectAssignmentType) === "1"
      ? "بازدید"
      : String(row?.projectAssignmentType) === "2"
        ? "تامین"
        : "-");
  const assignedPartyTypeLabel = (row) =>
    row?.assignedPartyTypeTitle ||
    (String(row?.assignedPartyType) === "1"
      ? "پیمانکار"
      : String(row?.assignedPartyType) === "2"
        ? "تامین‌کننده"
        : String(row?.assignedPartyType) === "3"
          ? "ناظر"
          : "-");
  const assignmentStatusLabel = (row) =>
    row?.projectAssignmentStatusTitle ||
    (String(row?.projectAssignmentStatus) === "1"
      ? "ارجاع شده"
      : String(row?.projectAssignmentStatus) === "2"
        ? "پذیرفته شده"
        : String(row?.projectAssignmentStatus) === "3"
          ? "رد شده"
          : String(row?.projectAssignmentStatus) === "4"
            ? "لغو شده"
            : String(row?.projectAssignmentStatus) === "5"
              ? "تکمیل شده"
              : String(row?.projectAssignmentStatus) === "6"
                ? "جایگزین شده"
                : "-");
  const quoteTypeLabel = (row) => row?.projectQuoteTypeTitle || "-";
  const quoteStatusLabel = (row) => row?.projectQuoteStatusTitle || "-";

  const refreshQuotes = useCallback(async () => {
    if (!request?.id) return;
    setQuoteLoading(true);
    try {
      const quoteRes = await ProjectWorkflowFoundationApi.getQuotesByRequestId(
        source,
        request.id
      );
      if (quoteRes.ok) {
        setQuoteList(quoteRes.data || []);
      }
    } finally {
      setQuoteLoading(false);
    }
  }, [request?.id, source]);

  const quoteStatusChipProps = (row) => {
    const status = Number(row?.projectQuoteStatus);
    if (status === QUOTE_STATUS_APPROVED) {
      return { color: "success", variant: "filled" };
    }
    if (status === QUOTE_STATUS_NEGOTIATING) {
      return { color: "info", variant: "filled" };
    }
    if (status === QUOTE_STATUS_SUBMITTED) {
      return { color: "warning", variant: "outlined" };
    }
    if (status === QUOTE_STATUS_REJECTED) {
      return { color: "error", variant: "outlined" };
    }
    return { color: "default", variant: "outlined" };
  };

  const canReviewQuote = (row) =>
    row?.canReview === true ||
    [QUOTE_STATUS_SUBMITTED, QUOTE_STATUS_NEGOTIATING].includes(
      Number(row?.projectQuoteStatus)
    );

  const canNegotiateQuote = (row) =>
    row?.canNegotiate === true ||
    [QUOTE_STATUS_SUBMITTED, QUOTE_STATUS_NEGOTIATING].includes(
      Number(row?.projectQuoteStatus)
    );

  const loadNegotiationThread = async (quoteId) => {
    if (!quoteId) return;
    setNegotiationLoading(true);
    setNegotiationError("");
    try {
      const res = await ProjectWorkflowFoundationApi.getNegotiationThread(
        source,
        quoteId
      );
      if (!res.ok) {
        setNegotiationError(res.message || "خطا در دریافت مذاکره");
        setNegotiationThread([]);
        return;
      }
      setNegotiationThread(res.data || []);
      await refreshQuotes();
    } catch {
      setNegotiationError("خطا در دریافت مذاکره.");
      setNegotiationThread([]);
    } finally {
      setNegotiationLoading(false);
    }
  };

  const openNegotiationDialog = async (quoteId) => {
    setQuoteNegotiationOpenId(quoteId);
    setNegotiationForm({
      messageType: "1",
      message: "",
      proposedPrice: "",
      proposedDurationDays: "",
    });
    await loadNegotiationThread(quoteId);
  };

  const closeNegotiationDialog = () => {
    setQuoteNegotiationOpenId(null);
    setNegotiationThread([]);
    setNegotiationError("");
  };

  const handleSendNegotiationMessage = async () => {
    if (!quoteNegotiationOpenId) return;
    if (!negotiationForm.message?.trim()) {
      setNegotiationError("متن پیام الزامی است.");
      return;
    }
    if (
      String(negotiationForm.messageType) === "2" &&
      !(Number(negotiationForm.proposedPrice) > 0)
    ) {
      setNegotiationError("برای پیشنهاد متقابل، مبلغ پیشنهادی الزامی است.");
      return;
    }

    setNegotiationSending(true);
    setNegotiationError("");
    try {
      const res = await ProjectWorkflowFoundationApi.addNegotiationMessage(
        source,
        {
          quoteId: quoteNegotiationOpenId,
          messageType: Number(negotiationForm.messageType),
          message: negotiationForm.message.trim(),
          proposedPrice:
            String(negotiationForm.messageType) === "2"
              ? Number(negotiationForm.proposedPrice)
              : null,
          proposedDurationDays: negotiationForm.proposedDurationDays
            ? Number(negotiationForm.proposedDurationDays)
            : null,
        }
      );
      if (!res.ok) {
        setNegotiationError(res.message || "ثبت پیام مذاکره با خطا مواجه شد.");
        return;
      }
      setNegotiationForm((prev) => ({
        ...prev,
        message: "",
        proposedPrice: "",
        proposedDurationDays: "",
      }));
      await loadNegotiationThread(quoteNegotiationOpenId);
      setQuoteActionSuccess(res.message || "پیام مذاکره ثبت شد.");
    } catch {
      setNegotiationError("خطا در ثبت پیام مذاکره.");
    } finally {
      setNegotiationSending(false);
    }
  };

  const showExecutionOnlyQuoteGuide =
    !operationalPlan &&
    (quoteList || []).some(
      (item) => item?.isActive && Number(item?.projectQuoteType) === QUOTE_TYPE_EXECUTION
    );

  const handleGenerateContract = async (quoteId) => {
    if (!quoteId) return;
    setQuoteActionError("");
    setQuoteActionSuccess("");
    setQuoteContractLoadingId(quoteId);
    try {
      const res = await ProjectWorkflowFoundationApi.generateContractFromQuote(
        source,
        quoteId
      );
      if (!res.ok) {
        setQuoteActionError(res.message || "ایجاد قرارداد با خطا مواجه شد.");
        return;
      }
      setQuoteActionSuccess(
        res.message ||
          (res.data?.wasCreated
            ? "قرارداد پروژه ایجاد شد."
            : "قرارداد پروژه به‌روزرسانی شد.")
      );
      await refreshQuotes();
    } catch {
      setQuoteActionError("خطا در ایجاد قرارداد از پیشنهاد قیمت.");
    } finally {
      setQuoteContractLoadingId(null);
    }
  };

  const handleAcceptQuote = async (quoteId) => {
    if (!quoteId) return;
    setQuoteActionError("");
    setQuoteActionSuccess("");
    setQuoteActionLoadingId(quoteId);
    try {
      const res = await ProjectWorkflowFoundationApi.acceptQuote(source, {
        quoteId,
        note: "",
      });
      if (!res.ok) {
        setQuoteActionError(res.message || "پذیرش پیشنهاد قیمت با خطا مواجه شد.");
        return;
      }
      setQuoteActionSuccess(res.message || "پیشنهاد قیمت پذیرفته شد.");
      await refreshQuotes();
    } catch {
      setQuoteActionError("خطا در پذیرش پیشنهاد قیمت.");
    } finally {
      setQuoteActionLoadingId(null);
    }
  };

  const openRejectQuoteDialog = (quoteId) => {
    setQuoteRejectTargetId(quoteId);
    setQuoteRejectReason("");
    setQuoteActionError("");
    setQuoteActionSuccess("");
    setQuoteRejectDialogOpen(true);
  };

  const handleConfirmRejectQuote = async () => {
    if (!quoteRejectTargetId) return;
    if (!quoteRejectReason?.trim()) {
      setQuoteActionError("دلیل رد پیشنهاد قیمت الزامی است.");
      return;
    }
    setQuoteActionLoadingId(quoteRejectTargetId);
    try {
      const res = await ProjectWorkflowFoundationApi.rejectQuote(source, {
        quoteId: quoteRejectTargetId,
        reason: quoteRejectReason.trim(),
      });
      if (!res.ok) {
        setQuoteActionError(res.message || "رد پیشنهاد قیمت با خطا مواجه شد.");
        return;
      }
      setQuoteRejectDialogOpen(false);
      setQuoteRejectTargetId(null);
      setQuoteRejectReason("");
      setQuoteActionSuccess(res.message || "پیشنهاد قیمت رد شد.");
      await refreshQuotes();
    } catch {
      setQuoteActionError("خطا در رد پیشنهاد قیمت.");
    } finally {
      setQuoteActionLoadingId(null);
    }
  };

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
          borderRadius: 2,
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          {/* Customer Info Header */}
          {!workspaceQuotesOnly && !workspaceOperationalBundle ? (
          <Box sx={{ mb: 2.5 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: "primary.lighter",
                borderRadius: 2,
                border: 1,
                borderColor: "primary.light",
              }}
            >
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="center"
              >
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: "primary.main",
                  }}
                >
                  <PersonIcon />
                </Avatar>
                <Typography variant="h6" fontWeight={700} color="primary.main">
                  {request?.fullName}
                </Typography>
              </Stack>
            </Paper>
          </Box>
          ) : null}

          {request?.referralSuggestedContractorCompanyName &&
          source !== "Customer" &&
          source !== "Contractor" ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              پیمانکار پیشنهادی سولارنت (ورود از لینک بازاریابی):{" "}
              <strong>{request.referralSuggestedContractorCompanyName}</strong>
            </Alert>
          ) : null}

          {canShowAssignment && !workspaceQuotesOnly && (
            <Box sx={{ mb: 2.5 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: 1,
                  borderColor: "divider",
                  backgroundColor: "background.default",
                }}
              >
                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                  <Typography variant="subtitle2" fontWeight={700}>
                    مسئول پیگیری:
                  </Typography>
                  <Chip
                    label={assignmentOwnerLabel}
                    color={
                      assignedId && currentUserId && assignedId === currentUserId
                        ? "success"
                        : assignedId
                          ? "default"
                          : "default"
                    }
                    variant={assignedId ? "filled" : "outlined"}
                  />
                  {request?.assignedAt && (
                    <Typography variant="body2" color="text.secondary">
                      تاریخ دریافت: {toJalaliFullDate(request.assignedAt)}
                    </Typography>
                  )}
                  {request?.assignmentSourceTitle && (
                    <Typography variant="body2" color="text.secondary">
                      وضعیت تخصیص: {request.assignmentSourceTitle}
                    </Typography>
                  )}
                </Stack>
              </Paper>
            </Box>
          )}

          {canShowOperationalPlanSection && (
            <Box sx={{ mb: 2.5 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: 1,
                  borderColor: "divider",
                  backgroundColor: "background.default",
                }}
              >
                <Stack spacing={1.5}>
                  {!workspaceQuotesOnly ? (
                  <>
                  <Typography variant="subtitle2" fontWeight={700}>
                    برنامه عملیاتی پروژه
                  </Typography>

                  {operationalPlanLoading ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">
                        در حال دریافت اطلاعات برنامه عملیاتی...
                      </Typography>
                    </Stack>
                  ) : null}

                  {operationalPlanError ? (
                    <Alert severity="error">{operationalPlanError}</Alert>
                  ) : null}

                  {operationalPlanSuccess ? (
                    <Alert severity="success">{operationalPlanSuccess}</Alert>
                  ) : null}

                  {showOperationalPlanSummary && operationalPlanSummary ? (
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Stack spacing={2}>
                          <Stack
                            direction="row"
                            flexWrap="wrap"
                            gap={1}
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
                              <Chip
                                size="small"
                                color="success"
                                label="برنامه عملیاتی ثبت شده"
                              />
                              {activeAssignmentCount > 0 ? (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`${activeAssignmentCount} ارجاع فعال`}
                                />
                              ) : null}
                            </Stack>
                            {operationalPlanSummary.savedAt ? (
                              <Typography variant="caption" color="text.secondary">
                                تاریخ ذخیره: {toJalaliFullDate(operationalPlanSummary.savedAt)}
                              </Typography>
                            ) : null}
                          </Stack>

                          {activeSiteVisitAssignment ? (
                            <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                              ارجاع بازدید اولیه در مرحله وام / مهرسان ثبت شده است.
                            </Alert>
                          ) : null}

                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary" display="block">
                                مسئول تأمین تجهیزات
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {operationalPlanSummary.equipmentSupply}
                              </Typography>
                            </Grid>
                            {operationalPlanSummary.supplyContractor ? (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  پیمانکار تأمین تجهیزات
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {operationalPlanSummary.supplyContractor}
                                </Typography>
                              </Grid>
                            ) : null}
                            {operationalPlanSummary.supplier ? (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  تأمین‌کننده
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {operationalPlanSummary.supplier}
                                </Typography>
                              </Grid>
                            ) : null}
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary" display="block">
                                پیمانکار اجرا
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {operationalPlanSummary.executionContractor}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary" display="block">
                                توضیحات مدیریتی
                              </Typography>
                              <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: "pre-wrap" }}>
                                {operationalPlanSummary.description}
                              </Typography>
                            </Grid>
                          </Grid>

                          <Stack direction="row" flexWrap="wrap" gap={1}>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={handleEditOperationalPlan}
                            >
                              ویرایش برنامه عملیاتی
                            </Button>
                            {activeAssignmentCount > 0 ? (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={scrollToAssignments}
                              >
                                مشاهده ارجاع‌ها
                              </Button>
                            ) : null}
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  ) : null}

                  {showOperationalPlanForm ? (
                  <Grid container spacing={1.5}>
                    {activeSiteVisitAssignment ? (
                      <Grid item xs={12}>
                        <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                          ارجاع بازدید اولیه در مرحله وام / مهرسان ثبت شده است.
                        </Alert>
                      </Grid>
                    ) : null}

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="equipment-supply-mode-label">
                          مسئول تأمین تجهیزات
                        </InputLabel>
                        <Select
                          labelId="equipment-supply-mode-label"
                          value={operationalPlanForm.equipmentSupplyMode}
                          label="مسئول تأمین تجهیزات"
                          onChange={(event) =>
                            handleOperationalPlanFieldChange(
                              "equipmentSupplyMode",
                              event.target.value
                            )
                          }
                        >
                          <MenuItem value="1">پیمانکار</MenuItem>
                          <MenuItem value="2">تأمین‌کننده</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {needsExecutionContractorForSupply ? (
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="supply-contractor-label">
                            پیمانکار تأمین تجهیزات/اجرا
                          </InputLabel>
                          <Select
                            labelId="supply-contractor-label"
                            value={operationalPlanForm.executionContractorInfoId}
                            label="پیمانکار تأمین تجهیزات/اجرا"
                            onChange={(event) =>
                              handleOperationalPlanFieldChange(
                                "executionContractorInfoId",
                                event.target.value
                              )
                            }
                          >
                            {(operationalPlanLookups.contractors || []).map((item) => (
                              <MenuItem key={item.id} value={String(item.id)}>
                                {formatContractorLookupLabel(item)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    ) : null}

                    {needsSupplierForSupply ? (
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="supplier-label">تأمین‌کننده</InputLabel>
                          <Select
                            labelId="supplier-label"
                            value={operationalPlanForm.supplierInfoId}
                            label="تأمین‌کننده"
                            onChange={(event) =>
                              handleOperationalPlanFieldChange(
                                "supplierInfoId",
                                event.target.value
                              )
                            }
                          >
                            {(operationalPlanLookups.suppliers || []).map((item) => (
                              <MenuItem key={item.id} value={String(item.id)}>
                                {item.title}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    ) : null}

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        multiline
                        minRows={2}
                        label="توضیحات مدیریتی"
                        value={operationalPlanForm.description}
                        onChange={(event) =>
                          handleOperationalPlanFieldChange(
                            "description",
                            event.target.value
                          )
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                        {workspaceOperationalBundle && operationalPlanUiMode === "edit" ? (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={operationalPlanSaving}
                            onClick={handleCancelOperationalPlanEdit}
                          >
                            انصراف
                          </Button>
                        ) : null}
                        <Chip
                          label={
                            operationalPlanSaving
                              ? "در حال ذخیره..."
                              : workspaceOperationalBundle && operationalPlanUiMode === "edit"
                                ? "ذخیره تغییرات"
                                : "ذخیره برنامه عملیاتی"
                          }
                          color="primary"
                          variant="filled"
                          onClick={operationalPlanSaving ? undefined : handleSaveOperationalPlan}
                          sx={{
                            cursor: operationalPlanSaving ? "default" : "pointer",
                            pointerEvents: operationalPlanSaving ? "none" : "auto",
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  ) : null}

                  <Box ref={assignmentsSectionRef}>
                    <Divider sx={{ my: 0 }} />
                  </Box>

                  <Typography variant="subtitle2" fontWeight={700}>
                    {workspaceOperationalBundle
                      ? "ارجاع‌های فعال پروژه"
                      : "ارجاع‌های فعلی پروژه"}
                  </Typography>
                  {assignmentLoading ? (
                    <Typography variant="body2" color="text.secondary">
                      در حال دریافت ارجاع‌ها...
                    </Typography>
                  ) : assignmentList.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      هنوز ارجاعی ثبت نشده است.
                    </Typography>
                  ) : (
                    <Grid container spacing={1}>
                      {(assignmentList || [])
                        .filter((item) => item?.isActive)
                        .map((item) => (
                          <Grid item xs={12} key={item.id}>
                            <Paper
                              variant="outlined"
                              sx={{ p: 1, display: "flex", flexWrap: "wrap", gap: 1 }}
                            >
                              <Chip size="small" label={`نوع: ${assignmentTypeLabel(item)}`} />
                              <Chip
                                size="small"
                                label={`گیرنده: ${assignedPartyTypeLabel(item)}`}
                                variant="outlined"
                              />
                              <Chip
                                size="small"
                                label={`نام: ${item?.assignedPartyName || "-"}`}
                                variant="outlined"
                              />
                              <Chip
                                size="small"
                                label={`ارجاع‌دهنده: ${item?.assignedByFullName || "-"}`}
                                variant="outlined"
                              />
                              <Chip
                                size="small"
                                label={`وضعیت: ${assignmentStatusLabel(item)}`}
                                color={item?.isActive ? "success" : "default"}
                                variant={item?.isActive ? "filled" : "outlined"}
                              />
                              <Chip
                                size="small"
                                label={`تاریخ ارجاع: ${toJalaliFullDate(item?.assignedAt) || "-"}`}
                                variant="outlined"
                              />
                            </Paper>
                          </Grid>
                        ))}
                    </Grid>
                  )}
                  </>
                  ) : null}

                  {(!workspaceOperationalBundle || workspaceQuotesOnly) && (
                  <>
                  {!workspaceQuotesOnly ? <Divider /> : null}

                  <Typography variant="subtitle2" fontWeight={700}>
                    پیشنهادهای قیمت پروژه
                  </Typography>
                  {showExecutionOnlyQuoteGuide && (
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      برنامه عملیاتی برای این پروژه ثبت نشده است؛ پیمانکاران فقط
                      می‌توانند پیشنهاد قیمت اجرا (ExecutionOnly) ثبت کنند. پس از
                      ثبت برنامه عملیاتی، انواع دیگر پیشنهاد قیمت نیز فعال
                      می‌شوند.
                    </Alert>
                  )}
                  {quoteActionError ? (
                    <Alert severity="error" onClose={() => setQuoteActionError("")}>
                      {quoteActionError}
                    </Alert>
                  ) : null}
                  {quoteActionSuccess ? (
                    <Alert
                      severity="success"
                      onClose={() => setQuoteActionSuccess("")}
                    >
                      {quoteActionSuccess}
                    </Alert>
                  ) : null}
                  {quoteLoading ? (
                    <Typography variant="body2" color="text.secondary">
                      در حال دریافت پیشنهادهای قیمت...
                    </Typography>
                  ) : quoteList.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      هنوز پیشنهادی برای این پروژه ثبت نشده است.
                    </Typography>
                  ) : (
                    <Grid container spacing={1}>
                      {(quoteList || [])
                        .filter((item) => item?.isActive)
                        .map((item) => (
                          <Grid item xs={12} key={item.id}>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1,
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1,
                                alignItems: "center",
                                borderWidth:
                                  Number(item?.projectQuoteStatus) ===
                                  QUOTE_STATUS_APPROVED
                                    ? 2
                                    : 1,
                                borderColor:
                                  Number(item?.projectQuoteStatus) ===
                                  QUOTE_STATUS_APPROVED
                                    ? "success.main"
                                    : "divider",
                                bgcolor:
                                  Number(item?.projectQuoteStatus) ===
                                  QUOTE_STATUS_APPROVED
                                    ? "success.50"
                                    : "background.paper",
                              }}
                            >
                              {Number(item?.projectQuoteStatus) ===
                              QUOTE_STATUS_APPROVED ? (
                                <Chip
                                  size="small"
                                  color="success"
                                  label="پیشنهاد پذیرفته‌شده"
                                />
                              ) : null}
                              <Chip size="small" label={`نوع: ${quoteTypeLabel(item)}`} />
                              {Number(item?.projectQuoteStatus) ===
                              QUOTE_STATUS_NEGOTIATING ? (
                                <Chip
                                  size="small"
                                  color="info"
                                  variant="filled"
                                  label="در حال مذاکره"
                                />
                              ) : null}
                              <Chip
                                size="small"
                                label={`وضعیت: ${quoteStatusLabel(item)}`}
                                {...quoteStatusChipProps(item)}
                              />
                              {item?.unreadNegotiationCount > 0 ? (
                                <Chip
                                  size="small"
                                  color="error"
                                  label={`${item.unreadNegotiationCount} پیام جدید`}
                                />
                              ) : null}
                              <Chip
                                size="small"
                                label={`نسخه: ${item?.revisionNo || 1}`}
                                variant="outlined"
                              />
                              <Chip
                                size="small"
                                label={`مبلغ پایه: ${item?.basePrice > 0 ? item.basePrice.toLocaleString("fa-IR") : "-"}`}
                                variant="outlined"
                              />
                              <Chip
                                size="small"
                                label={`مبلغ نهایی: ${item?.finalPrice > 0 ? item.finalPrice.toLocaleString("fa-IR") : "-"}`}
                                variant="outlined"
                              />
                              <Chip
                                size="small"
                                label={`پیمانکار: ${item?.contractorName || "-"}`}
                                variant="outlined"
                              />
                              <Chip
                                size="small"
                                label={`ثبت/ارسال: ${toJalaliFullDate(item?.submittedAt || item?.updateOn) || "-"}`}
                                variant="outlined"
                              />
                              {item?.acceptedAt ? (
                                <Chip
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  label={`پذیرش: ${toJalaliFullDate(item.acceptedAt) || "-"}${item?.acceptedByUserName ? ` (${item.acceptedByUserName})` : ""}`}
                                />
                              ) : null}
                              {item?.rejectedAt ? (
                                <Chip
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  label={`رد: ${toJalaliFullDate(item.rejectedAt) || "-"}${item?.rejectReason ? ` — ${item.rejectReason}` : ""}`}
                                />
                              ) : null}
                              {(item?.hasGeneratedContract || item?.projectContractId > 0) &&
                              item?.hasContractorAcceptance ? (
                                <Chip
                                  size="small"
                                  color="success"
                                  variant="filled"
                                  label={`قبول قرارداد توسط پیمانکار: ${toJalaliFullDate(item.contractorAcceptedAt) || "-"}${item?.contractorAcceptedByUserName ? ` (${item.contractorAcceptedByUserName})` : ""}`}
                                />
                              ) : (item?.hasGeneratedContract || item?.projectContractId > 0) ? (
                                <Chip
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  label="در انتظار قبول قرارداد توسط پیمانکار"
                                />
                              ) : null}
                              <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
                                {Number(item?.projectQuoteStatus) ===
                                QUOTE_STATUS_APPROVED ? (
                                  item?.hasGeneratedContract ||
                                  item?.projectContractId > 0 ? (
                                    <Button size="small" variant="outlined" disabled>
                                      قرارداد ایجاد شده
                                    </Button>
                                  ) : (
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="primary"
                                      disabled={quoteContractLoadingId === item.id}
                                      onClick={() => handleGenerateContract(item.id)}
                                    >
                                      {quoteContractLoadingId === item.id
                                        ? "..."
                                        : "ایجاد قرارداد"}
                                    </Button>
                                  )
                                ) : null}
                                {canNegotiateQuote(item) ? (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="info"
                                    onClick={() => openNegotiationDialog(item.id)}
                                  >
                                    مذاکره
                                  </Button>
                                ) : null}
                                {canReviewQuote(item) ? (
                                  <>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="success"
                                      disabled={quoteActionLoadingId === item.id}
                                      onClick={() => handleAcceptQuote(item.id)}
                                    >
                                      {quoteActionLoadingId === item.id
                                        ? "..."
                                        : "پذیرش"}
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      disabled={quoteActionLoadingId === item.id}
                                      onClick={() => openRejectQuoteDialog(item.id)}
                                    >
                                      رد
                                    </Button>
                                  </>
                                ) : null}
                              </Stack>
                            </Paper>
                          </Grid>
                        ))}
                    </Grid>
                  )}
                  </>
                  )}
                </Stack>
              </Paper>
            </Box>
          )}

          {canShowOperationalPlanSection &&
          request?.id &&
          !hideWorkflowV2Panel &&
          !workspaceOperationalBundle &&
          !workspaceQuotesOnly ? (
            <WorkflowV2IntegrationPanel
              requestId={request.id}
              source={source}
              showTimeline
            />
          ) : null}

          {/* Request Details */}
          {!workspaceOperationalBundle && !workspaceQuotesOnly ? (
          <>
          <Grid container spacing={2.5}>
            {/* Location Information */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  height: "100%",
                  backgroundColor: "background.default",
                  borderRadius: 2,
                  border: 1,
                  borderColor: "divider",
                  transition: "all 0.2s",
                  "&:hover": {
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                    borderColor: "primary.light",
                  },
                }}
              >
                <Stack spacing={1.5}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <LocationOnIcon color="primary" fontSize="small" />
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      color="text.primary"
                    >
                      اطلاعات تماس
                    </Typography>
                  </Box>
                  <Divider />

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      تعداد پیگیری
                    </Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ mt: 0.25 }}>
                      {request?.followUpCount ?? 0}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      آخرین پیگیری
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ mt: 0.25 }}>
                      {toJalaliFullDate(request?.lastFollowUpDate) || "ثبت نشده"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      پیگیری بعدی
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ mt: 0.25 }}>
                      {toJalaliFullDate(request?.nextFollowUpDate) || "ثبت نشده"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      نتیجه آخرین پیگیری
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ mt: 0.25 }}>
                      {request?.lastFollowUpResultTitle || "ثبت نشده"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      شهرستان
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{ mt: 0.25 }}
                    >
                      {request?.cityName || "نامشخص"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      آدرس
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{ mt: 0.25 }}
                    >
                      {request?.address || "نامشخص"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      کد پستی
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      fontFamily="monospace"
                      sx={{ mt: 0.25 }}
                    >
                      {request?.postalCode || "نامشخص"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      تلفن
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      fontFamily="monospace"
                      sx={{ mt: 0.25 }}
                    >
                      {request?.telephon || "-"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      موبایل
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      fontFamily="monospace"
                      sx={{ mt: 0.25 }}
                    >
                      {request?.mobile || "نامشخص"}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            {/* Request Information */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  height: "100%",
                  backgroundColor: "background.default",
                  borderRadius: 2,
                  border: 1,
                  borderColor: "divider",
                  transition: "all 0.2s",
                  "&:hover": {
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                    borderColor: "primary.light",
                  },
                }}
              >
                <Stack spacing={1.5}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <ElectricalServicesIcon color="primary" fontSize="small" />
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      color="text.primary"
                    >
                      اطلاعات درخواست
                    </Typography>
                  </Box>
                  <Divider />

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      وضعیت
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={request?.requestConfirmTitle || "نامشخص"}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      اولویت پیگیری
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={request?.leadPriorityTitle || "نیاز به بررسی"}
                        size="small"
                        color={request?.leadPriorityColor || "default"}
                        variant={
                          (request?.leadPriorityColor || "default") === "default"
                            ? "outlined"
                            : "filled"
                        }
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      امتیاز لید
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{ mt: 0.25 }}
                    >
                      {(request?.leadScore ?? 0).toString()} / 100
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      دلیل امتیاز
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      color="text.secondary"
                      sx={{ mt: 0.25 }}
                    >
                      {request?.leadScoreReasonSummary ||
                        "اطلاعات کافی برای اولویت‌بندی ثبت نشده"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      ظرفیت نیروگاه
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{ mt: 0.25 }}
                    >
                      {request?.plantCapacityTitle || "-"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      نوع نیروگاه
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{ mt: 0.25 }}
                    >
                      {request?.powerPlantTypeTitle || "ثبت نشده"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      کاربری محل نصب
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{ mt: 0.25 }}
                    >
                      {request?.locationUsageTypeTitle || "ثبت نشده"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      مساحت قابل نصب
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{ mt: 0.25 }}
                    >
                      {request?.installableAreaSquareMeter
                        ? `${request.installableAreaSquareMeter} متر مربع`
                        : "ثبت نشده"}
                    </Typography>
                  </Box>

                  {canShowOwnershipStatus && (
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                      >
                        وضعیت مالکیت
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{ mt: 0.25 }}
                      >
                        {request?.ownershipStatusTitle || "ثبت نشده"}
                      </Typography>
                    </Box>
                  )}

                  {canShowApplicantGoals && (
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                      >
                        هدف متقاضی
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{ mt: 0.25 }}
                      >
                        {request?.applicantGoalsTitle || "ثبت نشده"}
                      </Typography>
                    </Box>
                  )}

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      شماره پیگیری
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.5,
                      }}
                    >
                      <ConfirmationNumberIcon fontSize="small" color="action" />
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        fontFamily="monospace"
                      >
                        {request?.referenceID || "نامشخص"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      تاریخ اعتبار
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.5,
                      }}
                    >
                      <EventIcon fontSize="small" color="action" />
                      <Typography variant="body2" fontWeight={500}>
                        {toJalaliFullDate(request?.expireDate) || "نامشخص"}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          {canShowFollowUps ? (
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  backgroundColor: "background.default",
                  borderRadius: 2,
                  border: 1,
                  borderColor: "divider",
                }}
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  سوابق پیگیری
                </Typography>
                <RequestFollowUpTimeline followUps={followUps} />
              </Paper>
            </Grid>
          ) : null}
          </>
          ) : null}
        </CardContent>
      </Card>

      {/* Contractor Selection Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            minHeight: "80vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: 1,
            borderColor: "divider",
            pb: 2,
          }}
        >
          <Typography variant="h5" fontWeight={700} color="primary.main">
            انتخاب پیمانکار
          </Typography>
          <IconButton onClick={handleCloseModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <ContractorSelection requestId={request?.id} isModal={true} />
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!quoteNegotiationOpenId}
        onClose={closeNegotiationDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>مذاکره پیشنهاد قیمت</DialogTitle>
        <DialogContent dividers>
          {negotiationError ? (
            <Alert severity="error" sx={{ mb: 1 }}>
              {negotiationError}
            </Alert>
          ) : null}
          {negotiationLoading ? (
            <Typography variant="body2" color="text.secondary">
              در حال دریافت پیام‌ها...
            </Typography>
          ) : negotiationThread.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              هنوز پیامی در این مذاکره ثبت نشده است.
            </Typography>
          ) : (
            <Stack spacing={1.5} sx={{ mb: 2 }}>
              {negotiationThread.map((msg) => (
                <Paper
                  key={msg.id}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    alignSelf: msg.isMine ? "flex-end" : "flex-start",
                    maxWidth: "85%",
                    bgcolor: msg.isMine ? "primary.50" : "grey.50",
                    borderColor: msg.isMine ? "primary.light" : "divider",
                  }}
                >
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      {msg.senderUserName || "-"} — {msg.messageTypeTitle || "-"} —{" "}
                      {toJalaliFullDate(msg.createdAt) || "-"}
                      {msg.senderRole ? ` (${msg.senderRole})` : ""}
                    </Typography>
                    <Typography variant="body2">{msg.message}</Typography>
                    {msg.proposedPrice > 0 ? (
                      <Typography variant="caption" color="primary.main">
                        مبلغ پیشنهادی: {Number(msg.proposedPrice).toLocaleString("fa-IR")}
                      </Typography>
                    ) : null}
                    {msg.proposedDurationDays ? (
                      <Typography variant="caption" color="text.secondary">
                        مدت پیشنهادی: {msg.proposedDurationDays} روز
                      </Typography>
                    ) : null}
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}

          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            ارسال پیام جدید
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="negotiation-type-label">نوع پیام</InputLabel>
                <Select
                  labelId="negotiation-type-label"
                  label="نوع پیام"
                  value={negotiationForm.messageType}
                  onChange={(e) =>
                    setNegotiationForm((prev) => ({
                      ...prev,
                      messageType: e.target.value,
                    }))
                  }
                >
                  {negotiationMessageTypeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {String(negotiationForm.messageType) === "2" ? (
              <>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="مبلغ پیشنهادی"
                    value={negotiationForm.proposedPrice}
                    onChange={(e) =>
                      setNegotiationForm((prev) => ({
                        ...prev,
                        proposedPrice: e.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="مدت (روز)"
                    value={negotiationForm.proposedDurationDays}
                    onChange={(e) =>
                      setNegotiationForm((prev) => ({
                        ...prev,
                        proposedDurationDays: e.target.value,
                      }))
                    }
                  />
                </Grid>
              </>
            ) : null}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={3}
                label="متن پیام"
                value={negotiationForm.message}
                onChange={(e) =>
                  setNegotiationForm((prev) => ({
                    ...prev,
                    message: e.target.value,
                  }))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeNegotiationDialog}>بستن</Button>
          <Button
            variant="contained"
            disabled={negotiationSending}
            onClick={handleSendNegotiationMessage}
          >
            {negotiationSending ? "در حال ارسال..." : "ارسال پیام"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={quoteRejectDialogOpen}
        onClose={() => !quoteActionLoadingId && setQuoteRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>رد پیشنهاد قیمت</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="دلیل رد"
            fullWidth
            multiline
            minRows={3}
            value={quoteRejectReason}
            onChange={(e) => setQuoteRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setQuoteRejectDialogOpen(false)}
            disabled={!!quoteActionLoadingId}
          >
            انصراف
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmRejectQuote}
            disabled={!!quoteActionLoadingId}
          >
            تأیید رد
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RequestInfoCard;
